import {
  Button,
  ButtonGroup,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { ChangeEvent, useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import HttpHelper from '../../utils/HttpHelper';
import { handleDeleteClick } from '../../containers/ManageJobs/DeleteJob';
import { textFieldHandlerBuilder } from '../../utils/inputHandlers';

interface Column {
  id: string;
  label: string;
  minWidth?: number;
  align?: 'right' | 'center' | 'left';
}

interface Assessment {
  id: number;
  title: string;
  description: string;
  isLinkedToJob: boolean;
}

const columns: readonly Column[] = [
  {
    id: 'title',
    label: 'Title',
    align: 'left',
  },
  {
    id: 'actions',
    label: 'Actions',
    align: 'center',
  },
];

const ManageAssessmentsTable: React.FC = () => {
  const [assessmentList, setAssessmentList] = useState<Assessment[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedAssessmentId, setSelectedAssessmentId] = useState<
    number | null
  >(null);
  const [assessmentTitleQuery, setAssessmentTitleQuery] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);

  const updateAssessmentList = () => {
    const url =
      assessmentTitleQuery.trim().length === 0
        ? 'assessments'
        : `assessments?title=${assessmentTitleQuery}`;
    return HttpHelper.get(url);
  };
  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(true);
      updateAssessmentList().then(res => {
        setAssessmentList(res);
        setLoading(false);
      });
    }, 600);
    return () => {
      clearTimeout(timer);
    };
  }, [assessmentTitleQuery]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleOpenDialog = (id: number) => {
    setSelectedAssessmentId(id);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleConfirmDelete = async () => {
    if (selectedAssessmentId) {
      await HttpHelper.delete(`assessments/${selectedAssessmentId}/delete`);
      setAssessmentList(prevAssessmentList =>
        prevAssessmentList.filter(
          assessment => assessment.id !== selectedAssessmentId,
        ),
      );
    }
    setOpenDialog(false);
  };

  return (
    <Paper
      sx={{
        width: '100%',
        overflow: 'hidden',
        margin: 'auto',
        marginTop: '8px',
      }}
    >
      <TableContainer sx={{ maxHeight: '600px' }}>
        <Table stickyHeader aria-label="sticky-table">
          <TableHead>
            <TableRow>
              {columns.map((column, i) => (
                <TableCell
                  key={column.id}
                  align={column.align}
                >
                  {i === 0 ? (
                    <Grid container direction={'row'} columnGap={1}>
                      {column.label}
                      <TextField
                        placeholder="Search by title"
                        name="title"
                        type="text"
                        size="small"
                        value={assessmentTitleQuery}
                        onChange={textFieldHandlerBuilder(
                          setAssessmentTitleQuery,
                        )}
                      />
                    </Grid>
                  ) : (
                    column.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <Typography variant="h5" sx={{ margin: '10px 0' }}>
                Loading...
              </Typography>
            ) : assessmentList.length === 0 ? (
              <Typography variant="h5" sx={{ margin: '10px 0' }}>
                No assessments found
              </Typography>
            ) : (
              assessmentList
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map(assessment => {
                  return (
                    <TableRow
                      hover
                      role="checkbox"
                      tabIndex={-1}
                      key={assessment.id}
                    >
                      <TableCell key="title" align={columns[0].align}>
                        {assessment.title}
                      </TableCell>
                      <TableCell key="actions" sx={{ textAlign: 'center' }}>
                        <ButtonGroup>
                          <NavLink
                            to={`/manage-assessments/view/${assessment.id}`}
                            state={{
                              title: assessment.title,
                              description: assessment.description,
                            }}
                          >
                            <Button>View</Button>
                          </NavLink>
                          {assessment.isLinkedToJob ? (
                            <>
                              <Tooltip title="Disabled as assessment is linked to job(s)">
                                <span>
                                  <Button disabled={true}>Edit</Button>
                                </span>
                              </Tooltip>
                              <Tooltip title="Disabled as assessment is linked to job(s)">
                                <span>
                                  <Button disabled={true}>Delete</Button>
                                </span>
                              </Tooltip>
                            </>
                          ) : (
                            <>
                              <NavLink
                                to={`/manage-assessments/edit/${assessment.id}`}
                                state={{
                                  title: assessment.title,
                                  description: assessment.description,
                                }}
                              >
                                <Button>Edit</Button>
                              </NavLink>
                              <Button
                                onClick={() => handleOpenDialog(assessment.id)}
                              >
                                Delete
                              </Button>
                            </>
                          )}
                        </ButtonGroup>
                      </TableCell>
                    </TableRow>
                  );
                })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 100]}
        component="div"
        count={assessmentList.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{'Confirm Deletion'}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this assessment?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleConfirmDelete} autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default ManageAssessmentsTable;
